import React, { useEffect, useContext, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import FadeIn from 'react-fade-in';
import { useObserver } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import AppointmentGroupStore from '../../../../stores/AppointmentGroupStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as fn from '../../../../utilities/_functions';
import * as rts from '../../../../constants/routes';
import * as ah from '../../../../utilities/addressHelper';
import * as ch from '../../../../utilities/customerHelper';
import * as ph from '../../../../utilities/personHelper'
import * as bh from '../../../../utilities/badgeHelper'
import * as oh from '../../../../utilities/operationHelper';
import * as aph from '../../../../utilities/appointmentHelper';
import * as sys from '../../../../utilities/systemHelper';

import './GroupAppointments.scss';

function GroupAppointments(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const appointmentGroup = useContext(AppointmentGroupStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        quickDrawerFocus(props.drawer);
        return () => {
            isMounted.current = false;
            appointmentGroup.clear();
        }
    }, []) // eslint-disable-line

    const renderAppointmentDateTime = () => {
        if (!appointmentGroup.data.appointment.start) return null;

        const start = moment(appointmentGroup.data.appointment.start);
        const end = appointmentGroup.data.appointment.end ? moment(appointmentGroup.data.appointment.end) : null;
        const hasEndTime = !!end;
        const weekday = start.format('dddd');
        const day = start.format('D');
        const ordinal = start.format('Do').replace(day, '');
        const dateHtml = `${start.format('MMMM D')}<sup>${ordinal}</sup>${((start.year() !== moment().year()) ? `, ${start.format('YYYY')}` : '')}`;
        const startTimeHtml = `${start.format('h:mm')}${(!hasEndTime || start.format('a') !== end.format('a') ? ` ${start.format('a')}` : '')}`;
        const endTimeHtml = hasEndTime ? `${end.format('h:mm')} ${end.format('a')}` : '';

        return <ul className='list-inline no-style m-0'>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <small className='weekday'>{weekday}</small>
                    <span className='date' dangerouslySetInnerHTML={{ __html: dateHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>@</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: startTimeHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>to</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: endTimeHtml }}></span>
                </div>
            </li>
        </ul>
    }

    const renderResource = () => {
        const { resource } = appointmentGroup.data.appointment;

        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-block fw-500` + (resource && !resource.profilePictureUri ? ` profile-initials bg-color${resource.color}-500` : '')}
                    style={resource && resource.profilePictureUri ? {
                        backgroundImage: `url(${resource.profilePictureUri})`,
                        backgroundSize: 'cover',
                    } : null}
                    title={resource ? resource.fullName : 'System'}>
                    {resource && !resource.profilePictureUri ? <div className='d-initials'>{resource.initials}</div> : null}
                </span>
            </div>
            <div className='description'>
                <div className='name'>{resource.fullName}</div>
                <div className='services'>
                    {bh.renderAppointmentNew(appointmentGroup.data.appointment, 'mr-1')}
                    {bh.renderAppointmentFirstExam(appointmentGroup.data.appointment, 'mr-1')}
                    {bh.renderAppointmentPrebook(appointmentGroup.data.appointment, 'mr-1')}
                    {
                        appointmentGroup.data.appointment.services.map((s, si) => {
                            return <Fragment
                                key={`service_${si}`}
                            >
                                {bh.renderServiceCode(s, 'p-1 mr-1', (!s.isEligible || !aph.isEndState(appointmentGroup.data.appointment)))}
                            </Fragment>
                        })
                    }
                    {bh.renderAppointmentRescheduledByOffice(appointmentGroup.data.appointment, 'mr-1')}
                    {bh.renderAppointmentRescheduledByCustomer(appointmentGroup.data.appointment, 'mr-1')}
                </div>
            </div>
        </div>
    }

    const renderCustomer = () => {
        const customer = appointmentGroup.data.appointment.customer;
        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-flex profile-initials ${ch.getProfileBgColor(customer)} fw-500`}
                    title={customer.fullName}
                >
                    {customer.initials}
                </span>
            </div>
            <div className='description'>
                <Link
                    to={`${rts.Customers.Home}/${customer.id}`}
                    className='name text-gray-700 text-info-hover'
                    onClick={quickDrawer.deactivateAll}>{ph.getFullName(customer, true)}
                    {
                        customer.dateOfBirth || customer.sex || customer.gender || customer.pronoun ?
                            <small className='text-nowrap ml-2'>({`${ph.getAge(customer.dateOfBirth, moment(appointmentGroup.data.appointment.start))} ${ph.getSexGenderPronounDisplay(customer)}`.trim()})</small> : null
                    }
                </Link>
                {
                    customer.address && customer.address.country ?
                        <div className='info'>{ah.getAddressHtml(customer.address)}</div> : null
                }
                {
                    customer.emailAddress ?
                        <div className='info'>
                            <a
                                href={`mailto:${customer.emailAddress}`}
                            >{customer.emailAddress}
                            </a>
                        </div> : null
                }
                {
                    customer.phoneNumber ?
                        <div className='info'>
                            <a
                                href={`tel:${customer.phoneNumber}`}
                            >{sys.getFormattedPhoneNumber(customer.phoneNumber)}
                            </a>
                        </div> : null
                }
                {
                    customer.primaryContactPerson ?
                        <div className='mt-2 border-left border-3 border-gray-300 pl-3 py-1'>
                            <small className='text-primary-400 fs-75 text-uppercase d-block'>Primary Contact</small>
                            {
                                customer.primaryContactPerson.id ?
                                    <Link
                                        to={`${rts.Customers.Home}/${customer.primaryContactPerson.id}`}
                                        className='d-block fs-lg text-gray-700 text-info-hover'
                                        onClick={quickDrawer.deactivateAll}
                                    >
                                        {ph.getPreferredFirstLastName(customer.primaryContactPerson)}
                                        {
                                            customer.primaryContactPerson.relationship ?
                                                <small className='ml-1'>(<span className='fw-500 fs-90 text-success-700'>{customer.primaryContactPerson.relationship}</span>)</small> : null
                                        }
                                    </Link> :
                                    <div className='fs-lg text-gray-700 '>{ph.getPreferredFirstLastName(customer.primaryContactPerson)}
                                        {
                                            customer.primaryContactPerson.relationship ?
                                                <small className='ml-1'>(<span className='fw-500 fs-90 text-success-700'>{customer.primaryContactPerson.relationship}</span>)</small> : null
                                        }
                                    </div>
                            }
                            {
                                customer.primaryContactPerson.emailAddress ?
                                    <div className='info fs-95'>
                                        <a
                                            href={`mailto:${customer.primaryContactPerson.emailAddress}`}
                                        >{customer.primaryContactPerson.emailAddress}
                                        </a>
                                    </div> : null
                            }
                            {
                                customer.primaryContactPerson.phoneNumber ?
                                    <div className='info fs-95'>
                                        <a
                                            href={`tel:${customer.primaryContactPerson.phoneNumber}`}
                                        >{sys.getFormattedPhoneNumber(customer.primaryContactPerson.phoneNumber)}
                                        </a>
                                    </div> : null
                            }
                        </div> : null
                }
            </div>
        </div>
    }

    const handleGroupWithChange = (event, id) => {
        const index = appointmentGroup.groupWithIds.findIndex(i => id === i);
        const { appointment, suggestions } = appointmentGroup.data;
        const suggestion = suggestions.find(s => s.id === id);
        const { start: appointmentStart, end: appointmentEnd } = appointment;
        const { start: suggestionStart, end: suggestionEnd } = suggestion;
        const aStart = moment(appointmentStart);
        const aEnd = moment(appointmentEnd);
        const sStart = moment(suggestionStart);
        const sEnd = moment(suggestionEnd);

        if (index > -1) {
            appointmentGroup.groupWithIds.splice(index, 1);

            if (sStart.isBefore(aStart)) {
                const prevSuggestions = suggestions.filter(s => s.id !== id && moment(s.start).isSameOrAfter(sStart) && moment(s.start).isSameOrBefore(aStart));
                if (prevSuggestions && prevSuggestions.length > 0) {
                    for (let si = 0; si < prevSuggestions.length; si++) {
                        const pIndex = appointmentGroup.groupWithIds.findIndex(i => prevSuggestions[si].id === i);
                        appointmentGroup.groupWithIds.splice(pIndex, 1);
                    }
                }
            }
            if (sStart.isAfter(aEnd)) {
                const afterSuggestions = suggestions.filter(s => s.id !== id && moment(s.start).isSameOrBefore(sEnd) && moment(s.start).isSameOrAfter(aEnd));
                if (afterSuggestions && afterSuggestions.length > 0) {
                    for (let si = 0; si < afterSuggestions.length; si++) {
                        const pIndex = appointmentGroup.groupWithIds.findIndex(i => afterSuggestions[si].id === i);
                        appointmentGroup.groupWithIds.splice(pIndex, 1);
                    }
                }
            }
        }
        else {
            appointmentGroup.groupWithIds.push(id);

            if (sStart.isBefore(aStart)) {
                const prevSuggestions = suggestions.filter(s => s.id !== id && moment(s.start).isSameOrAfter(sStart) && moment(s.start).isSameOrBefore(aStart));
                if (prevSuggestions && prevSuggestions.length > 0) {
                    for (let si = 0; si < prevSuggestions.length; si++) {
                        appointmentGroup.groupWithIds.push(prevSuggestions[si].id);
                    }
                }
            }
            if (sStart.isAfter(aEnd)) {
                const afterSuggestions = suggestions.filter(s => s.id !== id && moment(s.start).isSameOrBefore(sEnd) && moment(s.start).isSameOrAfter(aEnd));
                if (afterSuggestions && afterSuggestions.length > 0) {
                    for (let si = 0; si < afterSuggestions.length; si++) {
                        appointmentGroup.groupWithIds.push(afterSuggestions[si].id);
                    }
                }
            }
        }

        appointmentGroup.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            appointmentGroup.save()
                .then(data => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: !!data, data: data });
                        }
                    }
                })
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                props.onCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={appointmentGroup.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('appointment')}
                        action='Group with'
                        category='Other Appointments'
                        className='appointments'
                        onCancel={props.onCancel}
                    />
                    <div className='quick-drawer-body'>
                        {
                            appointmentGroup.isReady ?
                                <FadeIn>
                                    <div className='group-appointments body-content'>
                                        <section className='date-time'>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    {renderAppointmentDateTime()}
                                                </div>
                                            </div>
                                        </section>
                                        <section className='resource'>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label><small>With</small></label>
                                                        {renderResource()}
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section className='customer'>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label><small>For</small></label>
                                                        {renderCustomer()}
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0 validate validate-required'>
                                                        <label className='required'><small>Group with</small></label>
                                                        {
                                                            appointmentGroup.data && appointmentGroup.data.suggestions && appointmentGroup.data.suggestions.length > 0 ?
                                                                appointmentGroup.data.suggestions.map((d, di) => {
                                                                    return <div
                                                                        key={`group-suggestions-${di}`}
                                                                        className='custom-control custom-checkbox mb-3'
                                                                    >
                                                                        <input
                                                                            id={`groupSuggestion-${di}`}
                                                                            type='checkbox'
                                                                            name='groupSuggestion'
                                                                            className='custom-control-input'
                                                                            value={d.id}
                                                                            checked={appointmentGroup.groupWithIds.some(g => g === d.id)}
                                                                            onChange={(e) => { handleGroupWithChange(e, d.id) }}
                                                                        />
                                                                        <label
                                                                            htmlFor={`groupSuggestion-${di}`}
                                                                            className='custom-control-label'
                                                                        >
                                                                            {sys.getFormattedLongDate(moment.utc(d.startUtc).local(), true)}<br />
                                                                            <div className='d-inline-block'>{ph.getFullName(d.customer, true)}</div>
                                                                            {
                                                                                d.services && d.services.length > 0 ?
                                                                                    <>
                                                                                        {
                                                                                            d.services.map((s, si) => {
                                                                                                return <span
                                                                                                    key={`group-suggestions-service-${si}`}
                                                                                                >
                                                                                                    {bh.renderServiceCode(s, 'ml-1')}
                                                                                                </span>
                                                                                            })
                                                                                        }
                                                                                    </> : null
                                                                            }
                                                                        </label>
                                                                    </div>
                                                                }) : null
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </FadeIn> : renderQuickDrawerLoading()
                        }
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={props.onCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                    >Continue</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default GroupAppointments;